import React, { useEffect, useState, useMemo } from 'react';
import api from '../lib/api';
import { useI18n } from '../context/I18nContext';
import WatchCard from '../components/WatchCard';
import { LUXURY_BRANDS } from '../lib/brands';
import { Search, X } from 'lucide-react';

export default function Catalog() {
  const { t } = useI18n();
  const [watches, setWatches] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('in_stock'); // 'in_stock' | 'on_request'
  const [filters, setFilters] = useState({ search: '', brand: '', condition: '', min_price: '', max_price: '' });

  const load = async () => {
    setLoading(true);
    const params = { availability: tab };
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) params[k] = v; });
    try {
      const { data } = await api.get('/watches', { params });
      setWatches(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { api.get('/watches/brands').then((r) => setBrands(r.data)).catch(() => {}); }, []);
  useEffect(() => { const id = setTimeout(load, 200); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [filters, tab]);

  // Combine inventory brands with curated luxury list (no duplicates, alphabetic)
  const allBrands = useMemo(() => {
    const set = new Set([...LUXURY_BRANDS, ...brands]);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [brands]);

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () => setFilters({ search: '', brand: '', condition: '', min_price: '', max_price: '' });

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-16 lg:py-24" data-testid="catalog-page">
      <div className="mb-12">
        <p className="gn-overline mb-4">{t('nav.catalog')}</p>
        <h1 className="font-serif text-5xl md:text-7xl text-[#f5f5f0] mb-6">{t('catalog.title')}</h1>
        <p className="text-lg text-[#a3b5ad] max-w-2xl font-light">{t('catalog.subtitle')}</p>
      </div>

      {/* Availability tabs */}
      <div className="flex border-b border-[#c9a961]/20 mb-12 gap-1" data-testid="availability-tabs">
        <button
          onClick={() => setTab('in_stock')}
          className={`px-8 py-4 text-xs tracking-[0.22em] uppercase font-medium border-b-2 -mb-px transition-colors ${tab === 'in_stock' ? 'border-[#c9a961] text-[#c9a961]' : 'border-transparent text-[#a3b5ad] hover:text-[#f5f5f0]'}`}
          data-testid="tab-in-stock"
        >
          {t('catalog.tab_in_stock')}
        </button>
        <button
          onClick={() => setTab('on_request')}
          className={`px-8 py-4 text-xs tracking-[0.22em] uppercase font-medium border-b-2 -mb-px transition-colors ${tab === 'on_request' ? 'border-[#c9a961] text-[#c9a961]' : 'border-transparent text-[#a3b5ad] hover:text-[#f5f5f0]'}`}
          data-testid="tab-on-request"
        >
          {t('catalog.tab_on_request')}
        </button>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-12">
        {/* FILTERS */}
        <aside className="space-y-8 border-t lg:border-t-0 lg:border-r border-[#c9a961]/15 lg:pr-8 pt-8 lg:pt-0" data-testid="catalog-filters">
          <div>
            <label className="gn-overline mb-3 block">{t('catalog.search')}</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#c9a961] pointer-events-none" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => update('search', e.target.value)}
                className="gn-input"
                style={{ paddingLeft: '2rem' }}
                placeholder=""
                data-testid="filter-search"
              />
            </div>
          </div>
          <div>
            <label className="gn-overline mb-3 block">Brand</label>
            <select value={filters.brand} onChange={(e) => update('brand', e.target.value)} className="gn-input bg-[#05150f]" data-testid="filter-brand">
              <option value="">{t('catalog.brand_all')}</option>
              {allBrands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="gn-overline mb-3 block">{t('catalog.condition')}</label>
            <select value={filters.condition} onChange={(e) => update('condition', e.target.value)} className="gn-input bg-[#05150f]" data-testid="filter-condition">
              <option value="">{t('catalog.condition_any')}</option>
              <option value="new">{t('catalog.condition_new')}</option>
              <option value="unworn">{t('catalog.condition_unworn')}</option>
              <option value="excellent">{t('catalog.condition_excellent')}</option>
              <option value="good">{t('catalog.condition_good')}</option>
            </select>
          </div>
          {tab === 'in_stock' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="gn-overline mb-3 block">{t('catalog.min_price')}</label>
                <input type="number" value={filters.min_price} onChange={(e) => update('min_price', e.target.value)} className="gn-input" placeholder="0" data-testid="filter-min-price" />
              </div>
              <div>
                <label className="gn-overline mb-3 block">{t('catalog.max_price')}</label>
                <input type="number" value={filters.max_price} onChange={(e) => update('max_price', e.target.value)} className="gn-input" placeholder="∞" data-testid="filter-max-price" />
              </div>
            </div>
          )}
          <button onClick={reset} className="text-xs tracking-[0.2em] uppercase text-[#c9a961] hover:text-[#d8c180] flex items-center gap-2" data-testid="filter-reset">
            <X size={14} /> {t('catalog.reset')}
          </button>
        </aside>

        {/* GRID */}
        <div data-testid="catalog-grid">
          <p className="text-sm text-[#a3b5ad] mb-8">{watches.length} {watches.length === 1 ? t('footer.piece_one') : t('footer.piece_other')}</p>
          {loading ? (
            <div className="text-[#c9a961] font-serif text-2xl">Loading...</div>
          ) : watches.length === 0 ? (
            <div className="py-20 text-center text-[#a3b5ad] font-serif text-2xl">{t('catalog.no_results')}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {watches.map((w, i) => <WatchCard key={w.id} watch={w} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
